import React, { useState, useEffect } from "react";
import { PlusCircle, Loader2, Pencil, Trash2 } from "lucide-react";
import { apiRequestBodyBuilder } from "../config/tableHelper";
import { IoIosArrowBack } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";

export default function GoogleAdsColumnsPage() {
  const [mode, setMode] = useState("");
  const [column, setColumn] = useState("");
  const [description, setDescription] = useState("");
  const [savedData, setSavedData] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupLoading, setGroupLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const columns = [
    "Campaign Name",
    "Ad Group",
    "Impressions",
    "Clicks",
    "CTR",
    "Cost",
    "Conversions",
    "Conversion Rate",
  ];

  // Fetch all groups
  const fetchGroups = async () => {
    let requestBody = apiRequestBodyBuilder();

    try {
      const res = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const data = await res.json();
      if (data?.groups) setGroups(data.groups);
    } catch (err) {
      console.error("Error fetching groups:", err);
    }
  };

  // Fetch saved columns
  const fetchSavedColumns = async () => {
    try {
      setLoading(true);

      let requestBody = apiRequestBodyBuilder(null);
      const res = await fetch(
        "https://api.confidanto.com/column-information/get",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const data = await res.json();
      if (Array.isArray(data)) setSavedData(data);
    } catch (err) {
      console.error("Error fetching saved columns:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchSavedColumns();
  }, []);

  // Create or update column info
  const handleSave = async () => {
    if (!column || !description) return;
    setGroupLoading(true);
    try {
      if (editingItem) {
        // 🔹 UPDATE existing column (Using new separate update API)
        const res = await fetch(
          "https://api.confidanto.com/column-information/update",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: editingItem.id,
              email: localStorage.getItem("email"),
              column_name: column,
              description: description,
            }),
          },
        );
        const data = await res.json();
        if (data?.message?.includes("success")) {
          await fetchSavedColumns();
          resetForm();
        } else {
          console.error("Update API Error:", data);
        }
      } else {
        // 🔹 CREATE new column
        const res = await fetch(
          "https://api.confidanto.com/column-information/create",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              project_id: localStorage.getItem("project_id"),
              group_id: selectedGroupId || "RI508VV96N",
              email: localStorage.getItem("email"),
              column_name: column,
              description: description,
            }),
          },
        );
        const data = await res.json();
        if (data?.message?.includes("success")) {
          await fetchSavedColumns();
          resetForm();
        } else {
          console.error("Create API Error:", data);
        }
      }
    } catch (err) {
      console.error("Error saving column info:", err);
    } finally {
      setGroupLoading(false);
    }
  };

  // Delete saved column
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this column?")) return;
    try {
      // 🔹 DELETE existing column (Using new separate delete API)
      const res = await fetch(
        "https://api.confidanto.com/column-information/delete",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: id,
            email: localStorage.getItem("email"),
          }),
        },
      );
      const data = await res.json();
      if (data?.message?.includes("success")) {
        await fetchSavedColumns();
      } else {
        console.error("Delete API Error:", data);
      }
    } catch (err) {
      console.error("Error deleting column:", err);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setMode("custom");
    setColumn(item.column_name);
    setDescription(item.description);
    setShowCreateGroup(true);
  };

  const resetForm = () => {
    setColumn("");
    setDescription("");
    setMode("");
    setSelectedGroupId(null);
    setEditingItem(null);
    setShowCreateGroup(false);
  };
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between bg-white shadow-sm p-6 rounded-xl border border-gray-200">
          <div className="flex flex-row   justify-center items-center">
            <button
              // onClick={navigateProjectList}
              onClick={() => {
                // console.log("PG TYPE: ", location, location.state.from);
                // return;
                navigate(location.state.from);
              }}
              className="mr-2 sm:mr-4 p-1.5 sm:p-2 transition-colors hover:bg-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
              aria-label="Go back"
            >
              <IoIosArrowBack className="h-6 w-6 sm:h-8 sm:w-8" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">
              Google Ads Column Description
            </h1>
          </div>
          <button
            onClick={() => setShowCreateGroup(!showCreateGroup)}
            className="flex items-center gap-2 bg-[#283593] text-white px-4 py-2 rounded-lg hover:bg-white hover:text-[#283593] hover:border-[#283593] border-1 transition-all"
          >
            <PlusCircle size={20} />
            {showCreateGroup ? "Close Form" : "Create Group"}
          </button>
        </div>

        {/* Create/Edit Column Form */}
        {showCreateGroup && (
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 animate-fadeIn">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Column Type
                </label>
                <select
                  value={mode}
                  onChange={(e) => {
                    setMode(e.target.value);
                    setColumn("");
                    setSelectedGroupId(null);
                  }}
                  className="w-full border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                >
                  <option value="">-- Select Type --</option>
                  <option value="group">Group Name (From API)</option>
                  <option value="predefined">Predefined Column</option>
                  <option value="custom">Write Your Own</option>
                </select>
              </div>

              {mode === "group" && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Select Group
                  </label>
                  <select
                    value={selectedGroupId || ""}
                    onChange={(e) => {
                      const selected = groups.find(
                        (g) => g.group_id === e.target.value,
                      );
                      setSelectedGroupId(selected.group_id);
                      setColumn(selected.group_name);
                    }}
                    className="w-full border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  >
                    <option value="">-- Choose a Group --</option>
                    {groups.map((group) => (
                      <option key={group.group_id} value={group.group_id}>
                        {group.group_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {mode === "predefined" && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Select Column
                  </label>
                  <select
                    value={column}
                    onChange={(e) => setColumn(e.target.value)}
                    className="w-full border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  >
                    <option value="">-- Choose a Column --</option>
                    {columns.map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {mode === "custom" && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Enter Column Name
                  </label>
                  <input
                    type="text"
                    value={column}
                    onChange={(e) => setColumn(e.target.value)}
                    placeholder="Enter your own column name"
                    className="w-full border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                  />
                </div>
              )}
            </div>

            <div className="mt-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Enter Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter column description here..."
                rows={3}
                className="w-full border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              />
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSave}
                disabled={groupLoading}
                className="bg-[#283593] text-white px-4 py-2 rounded-lg hover:bg-white hover:text-[#283593] hover:border-[#283593] border-1 transition-all flex items-center"
              >
                {groupLoading && (
                  <Loader2 className="animate-spin mr-2" size={18} />
                )}
                {editingItem ? "Update" : "Save"}
              </button>
            </div>
          </div>
        )}

        {/* Saved Columns */}
        <div className="bg-white p-6 shadow-sm rounded-xl border border-gray-200 relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Saved Column Descriptions
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fetchSavedColumns()}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Refresh
              </button>
              <button
                onClick={() => setSavedData([])}
                className="flex items-center gap-1 text-red-600 hover:text-red-800"
              >
                <Trash2 size={18} /> Clear All
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="animate-spin" size={20} /> Loading saved
              columns...
            </div>
          ) : savedData.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedData.map((item) => (
                <div
                  key={item.id}
                  className="relative border border-gray-200 bg-gray-50 rounded-lg p-4 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {item.column_name}
                  </h3>
                  <p className="text-gray-600 mt-1 text-sm">
                    {item.description}
                  </p>
                  <div className="text-xs text-gray-500 mt-2">
                    <span className="font-medium text-gray-700">Group:</span>{" "}
                    {item.group_name || "—"} <br />
                    <span className="font-medium text-gray-700">ID:</span>{" "}
                    {item.group_id}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">
              No saved column descriptions found.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
